const VISITOR_KEY = 'aaa_storefront_analytics_vid';
const SESSION_KEY = 'aaa_storefront_analytics_session';

// 'session' | 'local'. Switch to 'local' only once a cookie/consent banner ships: a
// persistent id is what turns "unique visitors" into unique people instead of unique
// tab-sessions, but it is also a stored identifier the storefront cannot yet ask about.
const VISITOR_STORE = 'session';

// The backend writes no TTL index on sessions, so a stored id stays resumable forever.
// Expiring it client-side is what keeps "one visit" from spanning days of tab restores.
const SESSION_IDLE_MS = 30 * 60 * 1000;

const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;
const VISITOR_ID_PATTERN = /^[A-Za-z0-9-]{8,64}$/;
const MAX_REFERRER_LENGTH = 2048;
const MAX_UTM_LENGTH = 256;

const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];
const CAMPAIGN_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
];

function storage(kind) {
    try {
        return (kind === 'local' ? window.localStorage : window.sessionStorage) || null;
    } catch {
        // Enterprise "block all site data" throws on the property access itself.
        return null;
    }
}

function readItem(kind, key) {
    try {
        const store = storage(kind);
        return store ? store.getItem(key) : null;
    } catch {
        return null;
    }
}

function writeItem(kind, key, value) {
    try {
        const store = storage(kind);
        if (!store) return false;
        store.setItem(key, value);
        return true;
    } catch {
        // Safari private mode throws on setItem rather than on access.
        return false;
    }
}

function removeItem(kind, key) {
    try {
        const store = storage(kind);
        if (store) store.removeItem(key);
    } catch {
        /* nothing to clear */
    }
}

function entryParams() {
    try {
        return new URLSearchParams(window.location.search || '');
    } catch {
        return new URLSearchParams('');
    }
}

function readEntryReferrer() {
    if (typeof document === 'undefined' || typeof window === 'undefined') return '';
    const raw = String(document.referrer || '').trim();
    if (!raw) return '';
    try {
        // Reloads and same-site navigations report our own origin, which the backend would
        // otherwise bucket as a `referral` from ourselves.
        if (new URL(raw).hostname === window.location.hostname) return '';
    } catch {
        return '';
    }
    return raw.slice(0, MAX_REFERRER_LENGTH);
}

function readEntryUtm(params) {
    const utm = {};
    UTM_KEYS.forEach((key) => {
        const raw = params.get(`utm_${key}`);
        if (!raw) return;
        const value = String(raw).trim().slice(0, MAX_UTM_LENGTH);
        if (value) utm[key] = value;
    });
    return Object.keys(utm).length > 0 ? utm : null;
}

function readCampaignKey(params) {
    const parts = CAMPAIGN_PARAMS.map((key) => String(params.get(key) || '').trim());
    return parts.some(Boolean) ? parts.join('|') : '';
}

function newVisitorId() {
    const webCrypto = typeof window !== 'undefined' ? window.crypto : null;

    // crypto.randomUUID needs a secure context, which a plain http:// LAN dev server is not.
    if (webCrypto?.randomUUID) return webCrypto.randomUUID();
    if (webCrypto?.getRandomValues) {
        const bytes = new Uint8Array(16);
        webCrypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 0x0f) | 0x40;
        bytes[8] = (bytes[8] & 0x3f) | 0x80;
        const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function readSessionRecord() {
    const raw = readItem('session', SESSION_KEY);
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
}

function clearSession() {
    removeItem('session', SESSION_KEY);
}

const entrySearch = typeof window !== 'undefined' ? entryParams() : new URLSearchParams('');
const ENTRY_CAMPAIGN_KEY = readCampaignKey(entrySearch);

/** The external referrer this document was opened from; '' for direct and same-site entries. */
export const ENTRY_REFERRER = readEntryReferrer();

/** utm_* values from the entry URL, or null. Captured once — pushState does not change them. */
export const ENTRY_UTM = readEntryUtm(entrySearch);

// Session.source is pinned when the session is created and preserved on every later write,
// so a returning visitor arriving on a new campaign inside a live tab would keep the old
// channel. Dropping the stored session is the only way to re-attribute them.
if (ENTRY_CAMPAIGN_KEY) {
    const record = readSessionRecord();
    if (record && record.campaignKey !== ENTRY_CAMPAIGN_KEY) {
        clearSession();
    }
}

let visitorId = null;
let visitorPersisted = false;

/** Stable visitor id for this browser, or null when storage refused to keep one. */
export function getVisitorId() {
    if (visitorId) return visitorPersisted ? visitorId : null;

    const stored = readItem(VISITOR_STORE, VISITOR_KEY);
    if (stored && VISITOR_ID_PATTERN.test(stored)) {
        visitorId = stored;
        visitorPersisted = true;
        return visitorId;
    }

    visitorId = newVisitorId();
    visitorPersisted = writeItem(VISITOR_STORE, VISITOR_KEY, visitorId);

    // Storage refused the write. Handing out a tab-local id would inflate visitor counts
    // without bound, so omit it and let the server derive one from ip + user agent instead.
    return visitorPersisted ? visitorId : null;
}

/** The live session id, or null when absent, malformed, or idle for over 30 minutes. */
export function readSessionId() {
    const record = readSessionRecord();
    if (!record) return null;

    const id = String(record.id || '');
    const lastSeenAt = Number(record.lastSeenAt);
    const fresh = Number.isFinite(lastSeenAt) && Date.now() - lastSeenAt <= SESSION_IDLE_MS;

    if (!OBJECT_ID_PATTERN.test(id) || !fresh) {
        clearSession();
        return null;
    }

    return id;
}

/** Stores the session id the server returned, refreshing the idle window. */
export function writeSessionId(id) {
    const value = String(id || '');
    // A non-ObjectId is silently ignored server-side and forks a second session, so storing
    // one would cost a session every pageview.
    if (!OBJECT_ID_PATTERN.test(value)) return;

    writeItem(
        'session',
        SESSION_KEY,
        JSON.stringify({ id: value, lastSeenAt: Date.now(), campaignKey: ENTRY_CAMPAIGN_KEY }),
    );
}
